import { useEffect } from "react";
import { CombinedGraphQLErrors } from "@apollo/client";
import { skipToken, useQuery } from "@apollo/client/react";
import styled from "@emotion/styled";
import { Chip, Variant as ChipVariant } from "@leafygreen-ui/chip";
import Refresh from "@via-ds/icons/Refresh";
import { useParams } from "react-router-dom";
import TaskStatusBadge from "@evg-ui/lib/components/Badge/TaskStatusBadge";
import { useErrorToast, useQueryParam } from "@evg-ui/lib/hooks";
import { TaskStatus } from "@evg-ui/lib/types/task";
import { useTaskAnalytics } from "analytics";
import { TTLInfo } from "components/404/TTLInfo";
import { ErrorBanner, ProjectBanner } from "components/Banners";
import { PatchAndTaskFullPageLoad } from "components/Loading/PatchAndTaskFullPageLoad";
import PageTitle from "components/PageTitle";
import {
  PageContent,
  PageLayout,
  PageSider,
  PageWrapper,
} from "components/styles";
import { DEFAULT_POLL_INTERVAL } from "constants/index";
import { slugs } from "constants/routes";
import { TaskQuery, TaskQueryVariables } from "gql/generated/types";
import { TASK } from "gql/queries";
import { usePolling } from "hooks";
import { useUpdateURLQueryParams } from "hooks/useUpdateURLQueryParams";
import { PageDoesNotExist } from "pages/NotFound";
import { RequiredQueryParams } from "types/task";
import { isPushCompletedVirtualTask } from "utils/tasks/virtualTasks";
import { ActionButtons } from "./task/ActionButtons";
import TaskPageBreadcrumbs from "./task/Breadcrumbs";
import ExecutionSelector from "./task/ExecutionSelector";
import { Metadata } from "./task/metadata";
import TaskTabs from "./task/taskTabs";
import { VirtualTaskBadge } from "./task/VirtualTaskBadge";

export const Task = () => {
  const { [slugs.taskId]: taskId } = useParams<{
    [slugs.taskId]: string;
  }>();
  const taskAnalytics = useTaskAnalytics();
  const updateQueryParams = useUpdateURLQueryParams();
  const [selectedExecution, setSelectedExecution] = useQueryParam<
    number | null
  >(RequiredQueryParams.Execution, null);

  // Query task data
  const { data, error, loading, refetch, startPolling, stopPolling } = useQuery<
    TaskQuery,
    TaskQueryVariables
  >(
    TASK,
    taskId
      ? {
          variables: { taskId: taskId, execution: selectedExecution },
          pollInterval: DEFAULT_POLL_INTERVAL,
          fetchPolicy: "cache-and-network",
          errorPolicy: "all",
        }
      : skipToken,
  );
  usePolling<TaskQuery, TaskQueryVariables>({
    startPolling,
    stopPolling,
    refetch,
  });

  useErrorToast(
    error,
    "Loading task",
    CombinedGraphQLErrors.is(error) &&
      error.errors.some((e) => !e?.path?.includes("annotation")),
  );

  const { task } = data ?? {};
  const {
    displayName,
    displayStatus,
    displayTask,
    errors,
    executionTasksFull,
    invalidatedByUpstream,
    isVirtual,
    latestExecution,
    priority,
    status,
    versionMetadata,
  } = task ?? {};

  // Update the default execution in the url if it isn't populated
  useEffect(() => {
    if (selectedExecution === null && task) {
      setSelectedExecution(task.latestExecution);
    }
  }, [task, selectedExecution, setSelectedExecution]);

  /**
   * Special handling for known issues and show the original status on the task page.
   */
  const shouldShowOriginalStatus = displayStatus === TaskStatus.KnownIssue;
  const isDisplayTask = executionTasksFull != null;

  if (!task && loading) {
    return <PatchAndTaskFullPageLoad />;
  }
  if (error && !task) {
    return (
      <PageWrapper omitPadding>
        <TTLInfo>
          <PageDoesNotExist />
        </TTLInfo>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ProjectBanner
        projectIdentifier={versionMetadata?.projectMetadata?.identifier}
      />
      {errors && errors.length > 0 && <ErrorBanner errors={errors} />}
      {task && (
        <TaskPageBreadcrumbs
          displayTask={task.displayTask || undefined}
          patchNumber={task.patchNumber || undefined}
          taskName={task.displayName}
          versionMetadata={task.versionMetadata}
        />
      )}
      <PageTitle
        badge={
          <StyledBadgeWrapper>
            <TaskStatusBadge
              status={
                (shouldShowOriginalStatus
                  ? status
                  : displayStatus) as TaskStatus
              }
            />
            {shouldShowOriginalStatus && (
              <TaskStatusBadge status={TaskStatus.KnownIssue} />
            )}
            {invalidatedByUpstream && (
              <Chip
                glyph={<Refresh />}
                label="Merge Queue Aborted"
                variant={ChipVariant.Gray}
              />
            )}
            {task && isVirtual && (
              <VirtualTaskBadge
                isPushCompleted={isPushCompletedVirtualTask(task)}
              />
            )}
          </StyledBadgeWrapper>
        }
        buttons={
          task ? (
            <ActionButtons
              // @ts-expect-error: FIXME. This comment was added by an automated script.
              initialPriority={priority}
              isDisplayTask={isDisplayTask}
              isExecutionTask={!!displayTask}
              task={task}
            />
          ) : undefined
        }
        loading={false}
        pageTitle={`Task${displayName ? ` - ${displayName}` : ""}`}
        title={displayName}
      />
      <PageLayout hasSider>
        <PageSider>
          {task &&
          latestExecution &&
          latestExecution > 0 &&
          selectedExecution !== null ? (
            <ExecutionSelector
              currentExecution={selectedExecution}
              latestExecution={latestExecution}
              taskId={task.id}
              updateExecution={(n: number) => {
                taskAnalytics.sendEvent({ name: "Changed execution" });
                updateQueryParams({
                  execution: `${n}`,
                });
              }}
            />
          ) : null}
          <Metadata error={error} loading={loading} task={task} />
        </PageSider>
        <StyledPageContent>
          {task && selectedExecution !== null && (
            <TaskTabs isDisplayTask={isDisplayTask} task={task} />
          )}
        </StyledPageContent>
      </PageLayout>
    </PageWrapper>
  );
};

const StyledBadgeWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: normal;
`;

const StyledPageContent = styled(PageContent)`
  // Unset overflow so that sticky header in Task History tab can work properly.
  overflow: unset;
`;
